// handles: which connection points a node renders on the canvas.
//   in  — has a top input handle (entry nodes don't)
//   out — array of output handles; a plain node has one ('out'), a branch
//         node has two named ones (logic-condition: yes/no, logic-ab: a/b),
//         logic-exit has none (nothing comes after it).
//
// comingSoon: true marks node types the journey engine can't execute yet
// (see pmw-click-backend/src/services/journeys/engine.service.js — it only
// runs linear, segment-triggered send_message steps, plus one condition type,
// 'if_not_opened', whose branches always reconverge to the same next step).
// These stay in the palette so journeys can still be sketched end-to-end, but
// are flagged rather than presented as if they'll actually run.
export const NODE_DEFS = {
  // Unified Entry block — one node whose config panel separates audience,
  // timing and re-entry (see ConfigDrawer's entry case). Replaces the three
  // separate entry-* palette items so an "audience AND schedule" entry no
  // longer needs two connected trigger nodes. The old entry-* defs below stay
  // for backward-compatible rendering of journeys saved before this change.
  'entry':           { group: 'entry',  label: 'Entry',             ico: 'login',          icoC: 'rgba(184,66,66,.15)',  icoT: '#C86060', accentType: 'entry',  hasBranches: false,                    handles: { in: false, out: ['out'] } },
  'entry-event':     { group: 'entry',  label: 'Event Trigger',     ico: 'bolt',           icoC: 'rgba(184,66,66,.15)',  icoT: '#C86060', accentType: 'entry',  hasBranches: false,                    handles: { in: false, out: ['out'] } },
  'entry-schedule':  { group: 'entry',  label: 'Schedule Entry',    ico: 'calendar_month', icoC: 'rgba(110,98,168,.15)', icoT: '#8878C0', accentType: 'entry',  hasBranches: false,                    handles: { in: false, out: ['out'] } },
  'entry-segment':   { group: 'entry',  label: 'Segment Entry',     ico: 'group',          icoC: 'rgba(72,120,176,.15)', icoT: '#6090C8', accentType: 'entry',  hasBranches: false,                    handles: { in: false, out: ['out'] } },
  'entry-api':       { group: 'entry',  label: 'API / Webhook',     ico: 'api',            icoC: 'rgba(122,110,176,.15)',icoT: '#A09AC8', accentType: 'entry',  hasBranches: false, comingSoon: true,  handles: { in: false, out: ['out'] } },
  // Push and In-app are ONE channel here, matching Campaigns exactly — a
  // "push" send always writes pickmywork-backend's in-app inbox row too
  // (push.provider.js), so there was never a real second channel to
  // configure separately; the old standalone In-App node's type/CTA fields
  // were UI-only and never read by any provider.
  'action-push':     { group: 'action', label: 'Push + In-app',     ico: 'notifications',  icoC: 'rgba(184,66,66,.12)',  icoT: '#C86060', accentType: 'action', hasBranches: false,                    handles: { in: true,  out: ['out'] } },
  'action-whatsapp': { group: 'action', label: 'WhatsApp',          ico: 'forum',          icoC: 'rgba(79,155,111,.12)', icoT: '#63B887', accentType: 'action', hasBranches: false,                    handles: { in: true,  out: ['out'] } },
  'action-email':    { group: 'action', label: 'Email',             ico: 'mail',           icoC: 'rgba(110,98,168,.12)', icoT: '#8878C0', accentType: 'action', hasBranches: false,                    handles: { in: true,  out: ['out'] } },
  'action-sms':      { group: 'action', label: 'SMS',               ico: 'sms',            icoC: 'rgba(160,124,48,.12)', icoT: '#C09848', accentType: 'action', hasBranches: false,                    handles: { in: true,  out: ['out'] } },
  'action-rcs':      { group: 'action', label: 'RCS',               ico: 'chat',           icoC: 'rgba(72,120,176,.12)', icoT: '#6090C8', accentType: 'action', hasBranches: false,                    handles: { in: true,  out: ['out'] } },
  'action-webhook':  { group: 'action', label: 'Webhook',           ico: 'webhook',        icoC: 'rgba(122,110,176,.12)',icoT: '#A09AC8', accentType: 'action', hasBranches: false, comingSoon: true,  handles: { in: true,  out: ['out'] } },
  'wait-relative':   { group: 'wait',   label: 'Relative Wait',     ico: 'hourglass_empty',icoC: 'rgba(160,124,48,.12)', icoT: '#C09848', accentType: 'wait',   hasBranches: false,                    handles: { in: true,  out: ['out'] } },
  'wait-absolute':   { group: 'wait',   label: 'Until Time',        ico: 'schedule',       icoC: 'rgba(160,124,48,.1)',  icoT: '#C09848', accentType: 'wait',   hasBranches: false,                    handles: { in: true,  out: ['out'] } },
  'logic-condition': { group: 'logic',  label: 'Condition Split',   ico: 'call_split',     icoC: 'rgba(79,155,111,.12)', icoT: '#63B887', accentType: 'logic',  hasBranches: true, branches: ['Yes','No'],  branchStyles: ['yes','no'], handles: { in: true, out: ['yes','no'] } },
  'logic-ab':        { group: 'logic',  label: 'A/B Split',         ico: 'science',        icoC: 'rgba(122,110,176,.12)',icoT: '#A09AC8', accentType: 'logic',  hasBranches: true, branches: ['A','B'],    branchStyles: ['va','vb'], handles: { in: true, out: ['a','b'] } },
  'logic-goal':      { group: 'logic',  label: 'Goal Check',        ico: 'ads_click',      icoC: 'rgba(79,155,111,.12)', icoT: '#63B887', accentType: 'logic',  hasBranches: false,                    handles: { in: true,  out: ['out'] } },
  'logic-exit':      { group: 'exit',   label: 'Exit Journey',      ico: 'logout',         icoC: 'rgba(80,80,80,.15)',   icoT: '#888888', accentType: 'exit',   hasBranches: false,                    handles: { in: true,  out: [] } },
};

export const ACCENT_COLORS = {
  entry:  '#C86060',
  action: '#5080B8',
  wait:   '#C09848',
  logic:  '#63B887',
  exit:   '#888888',
};

// Shapes match campaign.validator.js's real per-channel Joi schemas
// (push/whatsapp/sms/email) — node.cfg is passed straight through as the
// backend's `template` field (see utils/journeyConfig.js), so keeping the
// same shape here means it's already valid whenever journeys get wired onto
// the real provider layer.
export function defaultCfg(type) {
  const m = {
    'entry':           {
      mode: 'segment',
      segmentId: '', segmentName: '', membershipMode: 'current_and_future',
      event: 'app_install', lookbackValue: '7', lookbackUnit: 'days', propFilters: [],
      cron: '0 18 * * *', tz: 'IST +5:30', preset: '',
      attribute: '', fromValue: '', toValue: '', userIds: '',
      reEntry: 'once_ever', cooldownValue: '30', cooldownUnit: 'days', context: '',
      // global exit (journey-wide)
      globalExitEvent: '', globalExitMatchContext: false, globalExitDuration: '', globalExitDurationUnit: 'days',
      // shared sending rules (journey-wide)
      quietEnabled: false, quietStart: '22', quietEnd: '8',
      minSpacingValue: '', minSpacingUnit: 'hours',
      capPerDay: '', capChannel: 'any', onCap: 'skip', waDedupHours: '',
    },
    'entry-event':     { event: 'app_install', lookbackValue: '7', lookbackUnit: 'days', propFilters: [] },
    'entry-schedule':  { cron: '0 18 * * *', tz: 'IST +5:30', preset: '', segmentId: '' },
    'entry-segment':   { segmentId: '' },
    'entry-api':       { description: 'External CRM trigger', secret: '' },
    'action-push':     { title: '', body: '', redirectionType: '', redirectionValue: '', imageUrl: '' },
    'action-whatsapp': { templateName: '', templateParams: '', imageUrl: '', attributionLinkId: '' },
    'action-email':    { subject: '', html: '' },
    'action-sms':      { text: '', templateId: '', attributionLinkId: '' },
    'action-rcs':      { templateId: '', templateParams: '', attributionLinkId: '' },
    'action-webhook':  { url: '', method: 'POST', payload: '' },
    'wait-relative':   { amount: '1', unit: 'hours', waitType: 'Fixed duration' },
    'wait-absolute':   { rule: 'next_day_noon', tz: 'IST +5:30', time: '09:00', days: '1', weekday: '1', dayOfMonth: '1', daysBeforeEnd: '0', customDate: '', customTime: '' },
    'logic-condition': { condType: 'if_not_opened', event: '', windowValue: '', windowUnit: 'days', propFilters: [], attrField: '', attrOperator: 'equals', attrValue: '', timeWindow: 'ever' },
    'logic-ab':        { splitA: '50', splitB: '50' },
    'logic-goal':      { event: 'purchase' },
    'logic-exit':      { reason: 'journey_complete' },
  };
  const base = m[type] || {};
  // Repeat-on-Schedule applies to real message channels (not webhook).
  if (type.startsWith('action-') && type !== 'action-webhook') {
    return {
      ...base,
      repeatEnabled: false, repeatEvery: 'day', repeatAt: '09:00', repeatMax: '', repeatEndDate: '',
      sendWindowEnabled: false, sendWindowStart: '8', sendWindowEnd: '18',
    };
  }
  return { ...base };
}

export const PALETTE_GROUPS = [
  {
    label: 'Entry',
    nodes: ['entry'],
  },
  {
    label: 'Actions',
    nodes: ['action-push','action-whatsapp','action-email','action-sms','action-rcs','action-webhook'],
  },
  {
    label: 'Logic',
    nodes: ['wait-relative','wait-absolute','logic-condition','logic-ab','logic-goal','logic-exit'],
  },
];

// Node types that can be inserted mid-flow (quick-add from a connector,
// insert-on-edge) — everything except entry nodes (a journey has exactly one
// entry) and the coming-soon webhook.
export const ADDABLE_TYPES = [
  'action-push', 'action-whatsapp', 'action-email', 'action-sms', 'action-rcs',
  'wait-relative', 'wait-absolute', 'logic-condition', 'logic-ab', 'logic-goal', 'logic-exit',
];
